import React, { useState, useEffect } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import PackageForm, { PackageFormData } from '../components/packages/PackageForm';
import PackageList, { Package } from '../components/packages/PackageList';
import { usePackages } from '../hooks/usePackages';

const PackagesPage: React.FC = () => {
  const { packages, loading, guideId, loadPackages, createPackage, updatePackage, deletePackage } = usePackages();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPackage, setEditingPackage] = useState<Package | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<PackageFormData>({
    name: '',
    description: '',
    duration_months: 3,
    components: []
  });

  useEffect(() => {
    loadPackages();
  }, []);

  const handleCreatePackage = () => {
    setFormData({
      name: '',
      description: '',
      duration_months: 3,
      components: [
        { id: '', component_type: 'preparation', name: 'Preparation Sessions', quantity: 2, unit_price_dollars: 150, sort_order: 10 },
        { id: '', component_type: 'ceremony', name: 'Core Ceremony Sessions', quantity: 4, unit_price_dollars: 400, sort_order: 20 },
        { id: '', component_type: 'integration', name: 'Integration Sessions', quantity: 6, unit_price_dollars: 125, sort_order: 30 }
      ]
    });
    setEditingPackage(null);
    setShowCreateForm(true);
  };

  const handleEditPackage = (pkg: Package) => {
    setFormData({
      name: pkg.name,
      description: pkg.description || '',
      duration_months: pkg.duration_months,
      components: pkg.components
    });
    setEditingPackage(pkg);
    setShowCreateForm(true);
  };

  const handleDeletePackage = async (id: string) => {
    if (!confirm('Are you sure you want to delete this package?')) return;

    try {
      await deletePackage(id);
    } catch (error) {
      alert('Failed to delete package');
    }
  };

  const handleSavePackage = async () => {
    if (!formData.name || formData.components.length === 0) return;

    setSaving(true);
    try {
      if (editingPackage) {
        await updatePackage(editingPackage.id, formData);
      } else {
        await createPackage(formData);
      }
      
      setShowCreateForm(false);
      setEditingPackage(null);
    } catch (error) {
      alert('Failed to save package');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelForm = () => {
    setShowCreateForm(false);
    setEditingPackage(null);
  };

  return (
    <div className="p-6 bg-[#FFFCEF] text-[#2C2731] min-h-full">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-medium font-recoleta text-[#2C2731] mb-1">Packages</h1>
          <p className="text-sm text-[#2C2731]/80">Create and manage your service packages</p>
        </div>

        {/* Create New Package Section */}
        <div className="bg-[#FFFCEF] border border-[#2C2731] rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-medium text-[#2C2731] mb-1">Create New Package</h2>
              <p className="text-sm text-[#2C2731]/70">Package Name, Description, Program Length</p>
            </div>
            <button
              onClick={handleCreatePackage}
              className="inline-flex items-center px-4 py-2 border border-[#2C2731] bg-[#2C2731] text-[#FFFCEF] text-sm font-medium rounded-md hover:bg-[#241F2A] transition-colors"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Create New Package
            </button>
          </div>
        </div>

        {/* Create/Edit Package Form */}
        {showCreateForm && (
          <PackageForm
            formData={formData}
            setFormData={setFormData}
            isEditing={!!editingPackage}
            onSave={handleSavePackage}
            onCancel={handleCancelForm}
            loading={saving}
            guideId={guideId}
          />
        )}

        {/* Saved Packages */}
        <PackageList
          packages={packages}
          onEdit={handleEditPackage}
          onDelete={handleDeletePackage}
          onCreate={handleCreatePackage}
          loading={loading}
          guideId={guideId}
        />
      </div>
    </div>
  );
};

export default PackagesPage;
